import { useEffect, useRef, useState } from "react";
import { T } from "../lib/theme";
import { apiFetch } from "../lib/api";
import { Btn, Card } from "../components";

// ── Facebook blue button ───────────────────────────────────────────────────
const FbButton = ({ onClick, loading, label }) => (
  <button
    onClick={onClick}
    disabled={loading}
    style={{
      display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
      width: "100%", padding: "12px 20px", borderRadius: 10, cursor: loading ? "wait" : "pointer",
      background: loading ? "#5b9bd5" : "#1877f2", border: "none", color: "#fff",
      fontSize: 14, fontWeight: 700, transition: "background 0.15s",
    }}
    onMouseOver={e => { if (!loading) e.currentTarget.style.background = "#1565c0"; }}
    onMouseOut={e  => { if (!loading) e.currentTarget.style.background = "#1877f2"; }}
  >
    {/* Facebook "f" logo */}
    <svg width="18" height="18" viewBox="0 0 24 24" fill="#fff">
      <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/>
    </svg>
    {loading ? "Connecting…" : label}
  </button>
);

// ── Page selector card ─────────────────────────────────────────────────────
const PageSelector = ({ pages, onSelect, selecting }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
    {pages.map(p => (
      <div
        key={p.pageId}
        onClick={() => !selecting && onSelect(p.pageId)}
        style={{
          display: "flex", alignItems: "center", gap: 14,
          padding: "14px 16px", borderRadius: 12, cursor: selecting ? "wait" : "pointer",
          border: `1px solid ${T.border2}`, background: T.bg2,
          transition: "all 0.15s",
        }}
        onMouseOver={e => { if (!selecting) { e.currentTarget.style.borderColor = "#1877f2"; e.currentTarget.style.background = "rgba(24,119,242,0.06)"; }}}
        onMouseOut={e  => { e.currentTarget.style.borderColor = T.border2; e.currentTarget.style.background = T.bg2; }}
      >
        <div style={{ width: 40, height: 40, borderRadius: 10, background: "linear-gradient(135deg,#1877f2,#0052cc)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="#fff"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/></svg>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: T.text1 }}>{p.pageName}</div>
          <div style={{ fontSize: 11, color: T.text3, fontFamily: "monospace" }}>ID: {p.pageId}</div>
        </div>
        {selecting ? (
          <div style={{ width: 18, height: 18, border: `2px solid ${T.border2}`, borderTopColor: "#1877f2", borderRadius: "50%", animation: "spin 0.8s linear infinite", flexShrink: 0 }} />
        ) : (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={T.text3} strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>
        )}
      </div>
    ))}
  </div>
);

export default function MessengerPage({ token }) {
  const [status,    setStatus]    = useState(null);
  const [setup,     setSetup]     = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [oauthStep, setOauthStep] = useState("idle"); // idle | connecting | selecting | done
  const [pages,     setPages]     = useState([]);
  const [selecting, setSelecting] = useState(false);
  const [error,     setError]     = useState("");
  const [success,   setSuccess]   = useState("");
  const [copied,    setCopied]    = useState("");

  const loadStatus = () => Promise.all([
    apiFetch("/api/messenger/status", {}, token),
    apiFetch("/api/meta/setup", {}, token),
  ]).then(([s, su]) => { setStatus(s); setSetup(su); });

  // Load status whenever token changes
  useEffect(() => {
    if (!token) return;
    loadStatus().finally(() => setLoading(false));
  }, [token]);

  // Handle OAuth redirect — runs once when token becomes available
  const oauthHandled = useRef(false);
  useEffect(() => {
    if (!token || oauthHandled.current) return;
    const params    = new URLSearchParams(window.location.search);
    const metaOauth = params.get("meta_oauth");
    const platform  = params.get("platform");
    if (platform !== "messenger") return;
    oauthHandled.current = true;
    window.history.replaceState({}, "", window.location.pathname);

    if (metaOauth === "select") {
      setOauthStep("selecting");
      apiFetch("/api/meta/oauth/pages", {}, token)
        .then(d => {
          if (d.error) { setError(d.error); setOauthStep("idle"); return; }
          setPages(d.pages || []);
          if (d.pages?.length === 1) handleSelectPage(d.pages[0].pageId);
        })
        .catch(e => { setError(e.message); setOauthStep("idle"); });
    } else if (metaOauth === "error") {
      setError(params.get("msg") || "Facebook connection failed");
      setOauthStep("idle");
    }
  }, [token]);

  const startOAuth = async () => {
    setError(""); setOauthStep("connecting");
    try {
      const d = await apiFetch("/api/meta/oauth/start?platform=messenger", {}, token);
      if (d.error) { setError(d.error); setOauthStep("idle"); return; }
      window.location.href = d.authUrl;
    } catch (e) {
      setError(e.message || "Failed to start Facebook login");
      setOauthStep("idle");
    }
  };

  const handleSelectPage = async (pageId) => {
    setSelecting(true); setError("");
    try {
      const d = await apiFetch("/api/meta/oauth/connect", {
        method: "POST",
        body: JSON.stringify({ pageId }),
      }, token);
      if (d.error) { setError(d.error); return; }
      setSuccess(`Connected to "${d.pageName}" successfully!`);
      setOauthStep("done");
      await loadStatus();
    } catch (e) {
      setError(e.message || "Connection failed");
    } finally { setSelecting(false); }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect Facebook Messenger? The bot will stop replying to Messenger messages.")) return;
    await apiFetch("/api/messenger/disconnect", { method: "DELETE" }, token);
    setStatus(s => ({ ...s, connected: false, pageName: "", pageId: "" }));
    setSuccess("Disconnected.");
    setOauthStep("idle");
  };

  const copy = (text, key) => {
    navigator.clipboard.writeText(text).then(() => { setCopied(key); setTimeout(() => setCopied(""), 2000); });
  };

  if (loading) return (
    <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ width: 32, height: 32, border: `2px solid ${T.border2}`, borderTopColor: "#1877f2", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
    </div>
  );

  const webhookUrl  = setup?.webhookUrl  || "";
  const verifyToken = setup?.verifyToken || "";

  return (
    <div style={{ flex: 1, overflowY: "auto", padding: "26px 28px 50px" }}>

      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ width: 38, height: 38, borderRadius: 12, background: "linear-gradient(135deg,#1877f2,#0052cc)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="#fff"><path d="M12 2C6.477 2 2 6.145 2 11.259c0 2.913 1.453 5.512 3.726 7.21V22l3.405-1.869c.909.252 1.871.387 2.869.387 5.523 0 10-4.145 10-9.259S17.523 2 12 2zm1.008 12.457l-2.548-2.717-4.976 2.717 5.476-5.813 2.608 2.717 4.916-2.717-5.476 5.813z"/></svg>
          </div>
          <div>
            <div style={{ fontSize: 18, fontWeight: 800, color: T.text1 }}>Facebook Messenger</div>
            <div style={{ fontSize: 12, color: T.text3, marginTop: 2 }}>Connect your Facebook Page — bot replies to Messenger messages using your AI knowledge base</div>
          </div>
          {status?.connected && (
            <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 6, padding: "4px 12px", borderRadius: 20, background: "rgba(34,197,94,0.1)", border: "1px solid rgba(34,197,94,0.3)" }}>
              <span style={{ width: 7, height: 7, borderRadius: "50%", background: "#22c55e", animation: "pulse 2s infinite" }} />
              <span style={{ fontSize: 12, fontWeight: 600, color: "#22c55e" }}>Connected</span>
            </div>
          )}
        </div>
      </div>

      {/* Global messages */}
      {error   && <div style={{ padding: "10px 14px", borderRadius: 10, background: "rgba(244,63,94,0.08)", border: "1px solid rgba(244,63,94,0.3)", color: "#f43f5e", fontSize: 13, marginBottom: 16 }}>{error}</div>}
      {success && <div style={{ padding: "10px 14px", borderRadius: 10, background: "rgba(34,197,94,0.08)", border: "1px solid rgba(34,197,94,0.3)", color: "#22c55e", fontSize: 13, marginBottom: 16 }}>{success}</div>}

      {/* ── CONNECTED STATE ── */}
      {status?.connected ? (
        <div className="grid-2" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
          <Card>
            <div style={{ fontSize: 13, fontWeight: 600, color: T.text1, marginBottom: 16 }}>Page Connected</div>
            <div style={{ padding: "14px 16px", borderRadius: 12, background: T.bg0, border: `1px solid ${T.border1}`, marginBottom: 16 }}>
              <div style={{ fontSize: 11, color: T.text3, marginBottom: 4 }}>Facebook Page</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: T.text1 }}>{status.pageName || "—"}</div>
              <div style={{ fontSize: 12, color: T.text3, marginTop: 2, fontFamily: "monospace" }}>ID: {status.pageId}</div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", borderRadius: 9, marginBottom: 14,
              background: status.webhookSubscribed ? "rgba(34,197,94,0.06)" : "rgba(245,158,11,0.06)",
              border: `1px solid ${status.webhookSubscribed ? "rgba(34,197,94,0.25)" : "rgba(245,158,11,0.25)"}` }}>
              <span style={{ width: 6, height: 6, borderRadius: "50%", background: status.webhookSubscribed ? "#22c55e" : "#f59e0b", flexShrink: 0 }} />
              <span style={{ fontSize: 11, color: status.webhookSubscribed ? "#22c55e" : "#f59e0b", fontWeight: 600 }}>
                {status.webhookSubscribed ? "Webhook auto-subscribed" : "Webhook subscription pending"}
              </span>
            </div>
            <div style={{ fontSize: 12, color: T.text3, lineHeight: 1.7, marginBottom: 16 }}>
              Your AI bot is now active on Messenger. Incoming messages will be answered automatically using your knowledge base.
            </div>
            <Btn variant="danger" onClick={disconnect}>Disconnect Messenger</Btn>
          </Card>

          <Card>
            <div style={{ fontSize: 13, fontWeight: 600, color: T.text1, marginBottom: 16 }}>Webhook Info</div>
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: T.text2, marginBottom: 5 }}>Callback URL</div>
              <div style={{ display: "flex", gap: 6 }}>
                <input readOnly value={webhookUrl} style={{ flex: 1, padding: "8px 10px", borderRadius: 8, border: `1px solid ${T.border2}`, background: T.bg0, color: T.text2, fontSize: 11, fontFamily: "monospace", outline: "none" }} />
                <Btn variant="secondary" onClick={() => copy(webhookUrl, "url")}>{copied === "url" ? "Copied!" : "Copy"}</Btn>
              </div>
            </div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: T.text2, marginBottom: 5 }}>Verify Token</div>
              <div style={{ display: "flex", gap: 6 }}>
                <input readOnly value={verifyToken} style={{ flex: 1, padding: "8px 10px", borderRadius: 8, border: `1px solid ${T.border2}`, background: T.bg0, color: T.text2, fontSize: 11, fontFamily: "monospace", outline: "none" }} />
                <Btn variant="secondary" onClick={() => copy(verifyToken, "vt")}>{copied === "vt" ? "Copied!" : "Copy"}</Btn>
              </div>
            </div>
          </Card>
        </div>

      ) : oauthStep === "selecting" && pages.length > 1 ? (
        /* ── PAGE SELECTOR STATE ── */
        <div style={{ maxWidth: 480 }}>
          <Card>
            <div style={{ fontSize: 14, fontWeight: 700, color: T.text1, marginBottom: 6 }}>Select a Facebook Page</div>
            <div style={{ fontSize: 12, color: T.text3, marginBottom: 20, lineHeight: 1.6 }}>
              Choose which Facebook Page to connect your bot to:
            </div>
            <PageSelector pages={pages} onSelect={handleSelectPage} selecting={selecting} />
          </Card>
        </div>

      ) : (
        /* ── CONNECT STATE ── */
        <div className="grid-2" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
          <Card>
            <div style={{ fontSize: 13, fontWeight: 600, color: T.text1, marginBottom: 6 }}>Connect Your Facebook Page</div>
            <div style={{ fontSize: 12, color: T.text3, marginBottom: 20, lineHeight: 1.6 }}>
              Click below to log in with Facebook. You'll be asked to select which Page to connect — no tokens or manual setup needed.
            </div>

            <FbButton onClick={startOAuth} loading={oauthStep === "connecting"} label="Connect with Facebook" />

            <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "18px 0 14px" }}>
              <div style={{ flex: 1, height: 1, background: T.border1 }} />
              <span style={{ fontSize: 11, color: T.text3 }}>or use manual token</span>
              <div style={{ flex: 1, height: 1, background: T.border1 }} />
            </div>

            {/* Manual fallback — collapsed by default */}
            <ManualConnectForm token={token} onConnected={async () => { await loadStatus(); setSuccess("Connected successfully!"); }} />
          </Card>

          {/* How it works */}
          <Card>
            <div style={{ fontSize: 13, fontWeight: 600, color: T.text1, marginBottom: 16 }}>How it works</div>
            {[
              { n: 1, title: "Click Connect with Facebook",   desc: "You'll be redirected to Facebook login — no app setup needed on your end" },
              { n: 2, title: "Select your Facebook Page",     desc: "Choose which Page the bot should answer messages for" },
              { n: 3, title: "Bot is ready instantly",        desc: "Webhook is auto-configured. Your bot will reply to all Messenger messages using your AI knowledge base" },
            ].map(s => (
              <div key={s.n} style={{ display: "flex", gap: 12, marginBottom: 16, alignItems: "flex-start" }}>
                <div style={{ width: 26, height: 26, borderRadius: "50%", background: "rgba(24,119,242,0.12)", border: "1px solid rgba(24,119,242,0.3)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, color: "#1877f2", flexShrink: 0 }}>{s.n}</div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: T.text1, marginBottom: 3 }}>{s.title}</div>
                  <div style={{ fontSize: 12, color: T.text3, lineHeight: 1.6 }}>{s.desc}</div>
                </div>
              </div>
            ))}

            <div style={{ padding: "12px 14px", borderRadius: 10, background: "rgba(24,119,242,0.06)", border: "1px solid rgba(24,119,242,0.2)", marginTop: 4 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#1877f2", marginBottom: 4 }}>Webhook URL (for reference)</div>
              <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                <code style={{ flex: 1, fontSize: 10, color: T.text2, wordBreak: "break-all" }}>{webhookUrl}</code>
                <button onClick={() => copy(webhookUrl, "g")} style={{ padding: "3px 8px", borderRadius: 6, border: `1px solid ${T.border2}`, background: T.bg0, color: T.text3, cursor: "pointer", fontSize: 10 }}>{copied === "g" ? "Copied!" : "Copy"}</button>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}

// ── Manual token fallback (collapsed) ─────────────────────────────────────
function ManualConnectForm({ token, onConnected }) {
  const [open,            setOpen]            = useState(false);
  const [pageAccessToken, setPageAccessToken] = useState("");
  const [showToken,       setShowToken]       = useState(false);
  const [saving,          setSaving]          = useState(false);
  const [error,           setError]           = useState("");

  const connect = async () => {
    setError("");
    if (!pageAccessToken.trim()) return setError("Page Access Token is required");
    setSaving(true);
    try {
      const d = await apiFetch("/api/messenger/connect", {
        method: "POST",
        body: JSON.stringify({ pageAccessToken: pageAccessToken.trim() }),
      }, token);
      if (d.error) { setError(d.error); return; }
      setPageAccessToken("");
      onConnected();
    } catch (e) {
      setError(e.message || "Connection failed");
    } finally { setSaving(false); }
  };

  if (!open) return (
    <button onClick={() => setOpen(true)} style={{ background: "none", border: "none", color: T.text3, fontSize: 12, cursor: "pointer", padding: 0, textDecoration: "underline" }}>
      Paste token manually instead
    </button>
  );

  return (
    <div style={{ marginTop: 4 }}>
      {error && <div style={{ padding: "8px 12px", borderRadius: 8, background: "rgba(244,63,94,0.08)", border: "1px solid rgba(244,63,94,0.3)", color: "#f43f5e", fontSize: 12, marginBottom: 10 }}>{error}</div>}
      <label style={{ fontSize: 11, fontWeight: 600, color: T.text2, display: "block", marginBottom: 6 }}>Page Access Token</label>
      <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
        <input type={showToken ? "text" : "password"} value={pageAccessToken} onChange={e => setPageAccessToken(e.target.value)} placeholder="EAAxxxxxxxxxxxxxx..."
          style={{ flex: 1, padding: "9px 12px", borderRadius: 9, border: `1px solid ${T.border2}`, background: T.bg0, color: T.text1, fontSize: 13, outline: "none" }} />
        <button onClick={() => setShowToken(v => !v)} style={{ padding: "0 12px", borderRadius: 9, border: `1px solid ${T.border2}`, background: T.bg0, color: T.text3, cursor: "pointer", fontSize: 12 }}>
          {showToken ? "Hide" : "Show"}
        </button>
      </div>
      <Btn variant="primary" onClick={connect} disabled={saving}>{saving ? "Connecting..." : "Connect"}</Btn>
    </div>
  );
}
